import { Link } from 'react-router-dom';
import {
  BookOpen, Brain, CheckCircle2, ClipboardList, FileSearch,
  MessageSquare, ShieldAlert, Sparkles, TrendingUp, UserCheck,
} from 'lucide-react';
import {
  CTAPair, FaqAccordion, TestimonialBand,
  type Testimonial, type FaqItem,
} from '@kollez/design-system';
import { PageMeta } from '@/components/PageMeta';

/**
 * /ai — the AI platform marketing page.
 *
 * Design spec: docs/design/marketing/ai.md (Option A — Feature tour).
 *
 * Note: the authenticated Nexie chat surface lives at /nexie. This page
 * (/ai) is the public marketing umbrella that positions Nexie + the
 * broader AI capability across all three sides of Kollez.
 */

interface Persona { key: 'students' | 'partners' | 'institutions'; label: string; }

interface AiFeature {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  benefit: string;
  bullets: string[];
  personas: Persona[];
  status?: 'live' | 'preview';
}

const P_STUDENTS: Persona = { key: 'students', label: 'For students' };
const P_PARTNERS: Persona = { key: 'partners', label: 'For partners' };
const P_INSTITUTIONS: Persona = { key: 'institutions', label: 'For institutions' };

const FEATURES: AiFeature[] = [
  {
    icon: <MessageSquare size={22} />,
    eyebrow: 'Nexie',
    title: 'Career Advisor',
    benefit: 'Ask what to learn next. Get a plan grounded in your marks.',
    bullets: [
      'Reads your profile — marks, projects, target role',
      'Every answer cites the courses and openings it drew from',
      'Weekly plan you can actually follow',
    ],
    personas: [P_STUDENTS],
    status: 'live',
  },
  {
    icon: <BookOpen size={22} />,
    eyebrow: 'Nexie',
    title: 'Resume Builder',
    benefit: 'A resume that recruiters actually read — tailored per role.',
    bullets: [
      'Rewrites bullets for each opening you apply to',
      'ATS-checked so the software actually parses it',
      'One-click PDF, keeps your version history',
    ],
    personas: [P_STUDENTS],
    status: 'live',
  },
  {
    icon: <Brain size={22} />,
    eyebrow: 'Nexie',
    title: 'Mock Interviewer',
    benefit: 'Practice with an interviewer that asks follow-ups.',
    bullets: [
      'Role-specific — DSA, systems, ML, HR round',
      'Follow-ups tuned to your last answer',
      'Report with what to work on, not just a score',
    ],
    personas: [P_STUDENTS],
    status: 'live',
  },
  {
    icon: <ClipboardList size={22} />,
    eyebrow: 'Nexie',
    title: 'Auto-shortlist',
    benefit: 'Draft postings, get a ranked shortlist — you still approve.',
    bullets: [
      'Turns a role description into an eligibility filter',
      'Ranks candidates with reasons — never a black box',
      'You approve every shortlist before students see it',
    ],
    personas: [P_PARTNERS, P_INSTITUTIONS],
    status: 'preview',
  },
  {
    icon: <ShieldAlert size={22} />,
    eyebrow: 'Nexie',
    title: 'Fraud Detection',
    benefit: 'Flags fake jobs, spam applications, duplicate profiles.',
    bullets: [
      'Ambient — no one has to trigger it',
      'Human review before anything is removed',
      'Learns from your team\'s flags, not other tenants\'',
    ],
    personas: [P_STUDENTS, P_PARTNERS, P_INSTITUTIONS],
    status: 'live',
  },
  {
    icon: <TrendingUp size={22} />,
    eyebrow: 'Nexie',
    title: 'Group Insights',
    benefit: 'Reports that write themselves — with the numbers verified.',
    bullets: [
      'NIRF, NAAC, AICTE narratives drafted from your data',
      'Every figure links to the source row it came from',
      'Your team edits before it goes out',
    ],
    personas: [P_INSTITUTIONS],
    status: 'preview',
  },
];

const PILLARS: Array<{ title: string; body: string; icon: React.ReactNode }> = [
  {
    icon: <Sparkles size={20} />,
    title: 'Trained on Indian college data.',
    body: 'Curriculum, exam patterns, placement norms. Nexie understands that "8.4 CGPA at JNTU-H" means something specific.',
  },
  {
    icon: <FileSearch size={20} />,
    title: 'Grounded in your profile.',
    body: 'Every answer cites the marks, project, or opening it drew from. Nothing generic. Nothing generated from thin air.',
  },
  {
    icon: <UserCheck size={20} />,
    title: 'Never autonomous.',
    body: 'Every publish, every offer, every shortlist requires a human sign-off. Nexie proposes. You dispose.',
  },
];

const PERSONA_BENEFITS: Array<{ audience: string; headline: string; body: string; to: string; cta: string }> = [
  {
    audience: 'For students',
    headline: 'Learn faster. Land sooner.',
    body: 'Ask what to learn next. Draft a resume for each role. Practice with an interviewer that pushes back. All grounded in your marks, all free.',
    to: '/for-students',
    cta: 'Explore student features',
  },
  {
    audience: 'For partners',
    headline: 'Post, screen, shortlist — in minutes.',
    body: 'Turn a job description into a live posting. Get a ranked shortlist with reasons. You still approve every message that goes out.',
    to: '/for-partners',
    cta: 'Explore partner features',
  },
  {
    audience: 'For institutions',
    headline: 'Reports that write themselves.',
    body: 'NIRF, NAAC, AICTE narratives drafted from your own data. Every number cites the source row. Your team edits before it ships.',
    to: '/for-institutions',
    cta: 'Explore institution features',
  },
];

const ETHICS: Array<{ title: string; body: string }> = [
  {
    title: 'You always sign off.',
    body: 'Nexie proposes. Humans dispose. Every publish, offer, shortlist, and report needs a person to say yes before it moves.',
  },
  {
    title: 'Grounded, not generated.',
    body: 'Every claim cites the row it came from — visible to you, one click away. If Nexie can\'t find a source, it says so.',
  },
  {
    title: 'Fraud detection is ambient.',
    body: 'Nexie flags fake jobs, spam applications, duplicate profiles — all the time, in the background. A human always reviews before removal.',
  },
  {
    title: 'Your data trains only your workspace.',
    body: 'No cross-tenant learning. Your students\' data does not improve another college\'s Nexie. Ever.',
  },
];

const FAQ: FaqItem[] = [
  {
    q: 'Is Nexie a real product or vaporware?',
    a: 'Real. Career Advisor, Resume Builder, Mock Interviewer, and Fraud Detection are live today. Auto-shortlist and Group Insights are in preview with pilot partners. When you talk to us we will tell you exactly what is shipping.',
  },
  {
    q: 'Which AI models does Nexie use?',
    a: 'A mix, and the mix changes as better ones ship. What matters is that Nexie is grounded in your data (not a raw LLM), tuned on Indian education context, and audited every time it takes an action.',
  },
  {
    q: 'Does Nexie store my resume and college data?',
    a: 'Yes — inside your tenant workspace, in India. It is used to answer questions for you, and to flag fraud for your team. It never trains a model shared with other tenants.',
  },
  {
    q: 'Can Nexie hallucinate?',
    a: 'LLMs can. Nexie mitigates this by refusing to answer without a citation — every claim it makes is anchored to a row you can inspect. If it cannot find one, it says "I don\'t know" instead of making something up.',
  },
  {
    q: 'What happens if Nexie gets something wrong?',
    a: 'It cannot ship an offer, a shortlist, or a report by itself — a human on your team signs off first. If a signed-off action turns out to be wrong, every one has a 10-minute undo. Beyond that, contact your admin.',
  },
  {
    q: 'Can we turn Nexie off for our workspace?',
    a: 'Yes. Any feature — Auto-shortlist, Group Insights, all of Nexie — can be disabled per-tenant by your admin. The rest of Kollez works fine without it.',
  },
];

const AI_TESTIMONIALS: Testimonial[] = [
  {
    quote: 'The mock interviews are the best I have used. Nexie asked follow-ups on my answer — no other prep platform did that.',
    name: 'Harish T.',
    role: 'B.Tech ECE, 2025',
    org: 'Sri Vasavi Engineering College',
    number: 'Placed at Wipro',
  },
  {
    quote: 'We used to spend two days reading resumes for every drive. Nexie ranks them with reasons — we read the top 40, not the top 400.',
    name: 'Priya Ramesh',
    role: 'Campus TA lead',
    org: 'Wipro Elite NTH',
    number: '5x faster screening',
  },
  {
    quote: 'The NIRF draft used to eat a week of my time. Nexie writes the first version from our own data — I edit, I sign off, I move on.',
    name: 'Dr. Ramesh Malla',
    role: 'Director, Placements',
    org: 'Malla Reddy Group',
    number: '1 week → 1 morning',
  },
];

function PersonaChip({ p }: { p: Persona }) {
  const bg =
    p.key === 'students' ? 'rgb(var(--bg-success))'
    : p.key === 'partners' ? 'rgb(var(--bg-warning))'
    : 'rgb(var(--bg-info))';
  const fg =
    p.key === 'students' ? 'rgb(var(--ink-success))'
    : p.key === 'partners' ? 'rgb(var(--ink-warning))'
    : 'rgb(var(--ink-accent))';
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: bg, color: fg }}
    >
      {p.label}
    </span>
  );
}

function NexieHeroMock() {
  return (
    <figure className="card overflow-hidden shadow-md self-center w-full max-w-[520px] justify-self-end" aria-label="Nexie career advisor example">
      <div className="flex items-center gap-3 border-b border-line-tertiary px-5 py-3.5">
        <span
          className="flex items-center justify-center rounded-full"
          style={{
            width: 34, height: 34,
            background: 'rgb(var(--bg-info))',
            color: 'rgb(var(--ink-accent))',
          }}
        >
          <Sparkles size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-sm">Nexie · Career Advisor</div>
          <div className="text-xs text-ink-tertiary">Grounded in your profile · cites every source</div>
        </div>
        <span className="tier">Live</span>
      </div>
      <div className="bg-primary p-5 grid gap-3.5 text-sm">
        <div
          className="rounded-2xl rounded-br-sm px-3.5 py-2.5 self-end max-w-[85%]"
          style={{ background: 'rgb(var(--bg-tertiary))', color: 'rgb(var(--ink-primary))' }}
        >
          What should I learn next to become a data engineer?
        </div>
        <div
          className="rounded-2xl rounded-bl-sm px-3.5 py-3 max-w-[92%]"
          style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
        >
          <p className="m-0 mb-2">Based on your marks in databases and Python, here's a 6-week path:</p>
          <ol className="m-0 pl-5 grid gap-1">
            <li>SQL for analytics — 2 weeks · <span className="link">AWS course</span> · badge on complete</li>
            <li>Python data engineering — 2 weeks · <span className="link">DataCamp partner</span></li>
            <li>Cloud data warehousing — 2 weeks · <span className="link">GCP path</span></li>
          </ol>
          <p className="mt-2 mb-0 text-xs text-ink-tertiary">Cited from: your profile · the AWS Cloud track · 3 open Data Analyst listings</p>
        </div>
      </div>
    </figure>
  );
}

export function AiPage() {
  return (
    <>
      <PageMeta
        title="AI for education"
        description="Kollez is an AI-first education platform. Nexie stitches your marks, openings, and reports together with intelligence — grounded, cited, and never autonomous."
        canonical="/ai"
      />

      {/* HERO */}
      <section className="mx-auto grid max-w-page-wide gap-12 px-5 pb-16 pt-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10 lg:pb-24 lg:pt-20">
        <div className="flex flex-col justify-center">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            AI for education
          </p>
          <h1 className="display text-ink-primary mt-2">AI that actually understands college.</h1>
          <p className="mt-6 max-w-[38rem] text-lg leading-8 text-ink-secondary">
            Nexie is Kollez's AI — tuned on Indian education data, grounded in your
            profile, and never autonomous. It drafts, it ranks, it explains. You
            always sign off.
          </p>
          <div className="mt-8">
            <CTAPair
              primary={{ label: 'Talk to Nexie', to: '/register/student' }}
              secondary={{ label: 'See how it works', to: '#features' }}
            />
          </div>
          <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-secondary" aria-label="AI safeguards">
            <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-ink-success" />Cites every source</li>
            <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-ink-success" />Human sign-off on every action</li>
            <li className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-ink-success" />No cross-tenant training</li>
          </ul>
        </div>

        <NexieHeroMock />
      </section>

      {/* FEATURE GRID */}
      <section id="features" className="border-y border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <div className="max-w-2xl">
            <p className="eyebrow">The Nexie surface</p>
            <h2 className="h-serif text-3xl lg:text-4xl mt-2">Six ways Nexie does the work.</h2>
            <p className="mt-3 text-ink-secondary">
              One assistant. Different jobs, depending on who's asking. Every
              answer cites its source; every action needs a human sign-off.
            </p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <article
                key={f.title}
                className="card card-pad grid gap-3 h-full"
                style={{ transition: 'transform var(--dur-2, 220ms) var(--ease-out)' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <span
                    className="flex items-center justify-center rounded-lg"
                    style={{
                      width: 42, height: 42,
                      background: 'rgb(var(--bg-info))',
                      color: 'rgb(var(--ink-accent))',
                    }}
                  >
                    {f.icon}
                  </span>
                  {f.status === 'preview' ? (
                    <span
                      className="text-[0.6875rem] font-medium uppercase tracking-wide rounded-full px-2 py-0.5"
                      style={{ background: 'rgb(var(--bg-warning))', color: 'rgb(var(--ink-warning))' }}
                    >
                      Preview
                    </span>
                  ) : (
                    <span
                      className="text-[0.6875rem] font-medium uppercase tracking-wide rounded-full px-2 py-0.5"
                      style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-success))' }}
                    >
                      Live
                    </span>
                  )}
                </div>
                <p className="eyebrow m-0">{f.eyebrow}</p>
                <h3 className="font-serif text-xl m-0">{f.title}</h3>
                <p className="text-ink-secondary m-0">{f.benefit}</p>
                <ul className="grid gap-1.5 mt-1 text-sm text-ink-secondary">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <CheckCircle2 size={14} className="mt-0.5 text-ink-success flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-1.5 mt-2 pt-3 border-t border-line-tertiary">
                  {f.personas.map((p) => <PersonaChip key={p.key} p={p} />)}
                </div>
              </article>
            ))}
          </div>
          <p className="mt-8 text-sm text-ink-tertiary">
            The Mock Interviewer runs on Kollez's assessments engine — see{' '}
            <Link to="/assessments" className="link">all six assessment modes</Link>{' '}
            for the full picture.
          </p>
        </div>
      </section>

      {/* HOW NEXIE IS DIFFERENT */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">How Nexie is different</p>
          <h2 className="h-serif text-3xl lg:text-4xl mt-2">Not a chatbot dropped on top.</h2>
          <p className="mt-3 text-ink-secondary">
            Kollez is AI-first — Nexie is the reasoning layer that turns raw
            college and hiring data into next steps. Three commitments make it
            different from a generic assistant.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {PILLARS.map((p) => (
            <article key={p.title} className="grid gap-3">
              <span
                className="flex items-center justify-center rounded-lg"
                style={{
                  width: 40, height: 40,
                  background: 'rgb(var(--bg-info))',
                  color: 'rgb(var(--ink-accent))',
                }}
              >
                {p.icon}
              </span>
              <h3 className="font-serif text-xl m-0">{p.title}</h3>
              <p className="text-ink-secondary m-0">{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* PERSONA BENEFITS */}
      <section className="border-y border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <div className="max-w-2xl">
            <p className="eyebrow">One AI, three sides</p>
            <h2 className="h-serif text-3xl lg:text-4xl mt-2">Same Nexie. Different job.</h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {PERSONA_BENEFITS.map((b) => (
              <article key={b.audience} className="card card-pad grid gap-3 h-full">
                <p className="eyebrow m-0">{b.audience}</p>
                <h3 className="font-serif text-xl m-0">{b.headline}</h3>
                <p className="text-ink-secondary m-0 flex-1">{b.body}</p>
                <Link to={b.to} className="link text-sm mt-2">{b.cta} →</Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ETHICS */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20">
        <div className="max-w-2xl">
          <p className="eyebrow">Ethics &amp; safeguards</p>
          <h2 className="h-serif text-3xl lg:text-4xl mt-2">What Nexie will not do.</h2>
          <p className="mt-3 text-ink-secondary">
            AI in education raises real concerns. Here is how we address them
            without hand-waving.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {ETHICS.map((e) => (
            <article key={e.title} className="grid gap-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="mt-0.5 text-ink-success flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-lg m-0">{e.title}</h3>
                  <p className="text-ink-secondary m-0 mt-1">{e.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-wide px-5 lg:px-10">
          <div className="max-w-2xl mb-8">
            <p className="eyebrow">Nexie in the field</p>
            <h2 className="h-serif text-3xl lg:text-4xl mt-2">One AI. Three kinds of user. Three kinds of relief.</h2>
          </div>
          <TestimonialBand quotes={AI_TESTIMONIALS} />
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-8 lg:grid-cols-[0.6fr_1fr]">
          <div>
            <p className="eyebrow">Common questions</p>
            <h2 className="h-serif text-3xl lg:text-4xl mt-2">The honest answers.</h2>
            <p className="mt-3 text-ink-secondary">
              Six questions we get most. If yours isn't here, ask us — we'd
              rather tell you what we don't know than pretend we do.
            </p>
          </div>
          <FaqAccordion items={FAQ} />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line-tertiary bg-primary py-16 lg:py-20">
        <div className="mx-auto max-w-page-wide px-5 lg:px-10 text-center">
          <h2 className="h-serif text-3xl lg:text-4xl">Try Nexie yourself.</h2>
          <p className="mt-3 max-w-2xl mx-auto text-ink-secondary">
            The best way to see what AI-first education means is to ask Nexie a
            question. Students get free access. Institutions and partners can
            book a 30-minute demo.
          </p>
          <div className="mt-8">
            <CTAPair
              primary={{ label: 'Talk to Nexie — free', to: '/register/student' }}
              secondary={{ label: 'Book a 30-min demo', to: '/contact?topic=demo' }}
            />
          </div>
        </div>
      </section>
    </>
  );
}
